import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts';
import { LoginLoading } from '../LoginLoading';
import { login } from '@/api/turbopro';
import { HttpError } from '@/api/envelope';
import { rearmarSessaoExpirada } from '@/api/sessao-expirada';
import { gravarLogin } from '@/auth/session-store';
import { LoginFormData, loginFormValidationSchema } from '../validators/loginValidator';

export function Login() {
  const { refrescarUsuario } = useAuth();
  const navigate = useNavigate();

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginFormValidationSchema),
    mode: 'onSubmit',
    defaultValues: {
      user: '',
      pass: '',
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = loginForm;

  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handlePassVisible = () => {
    setPasswordVisibility(!passwordVisibility);
  };

  const handleLogin = async (data: LoginFormData) => {
    setLoginError('');
    setIsLoading(true);

    try {
      const dados = await login(data.user, data.pass);
      // A seleção de gestor não faz mais parte do login: entra direto e o
      // gate `RequireGestor` cuida de pedir o gestor quando necessário.
      gravarLogin(dados.emailLogin || data.user);
      refrescarUsuario();
      rearmarSessaoExpirada();
      setShowLoading(true);
    } catch (falha) {
      setLoginError(
        falha instanceof HttpError && falha.message
          ? falha.message
          : 'Erro ao fazer login. Verifique suas credenciais.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadingComplete = () => {
    // Redirecionar para home após login
    navigate('/');
  };

  if (showLoading) {
    return <LoginLoading onLoadingComplete={handleLoadingComplete} />;
  }

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-linear-to-br from-[#9ebe3b] via-[#7da32e] to-[#6b8e28] grid grid-cols-1 lg:grid-cols-2">
      {/* Formas flutuantes animadas - apenas desktop */}
      <div className="hidden lg:block absolute w-[450px] h-[450px] rounded-full bg-white/15 -top-[120px] -left-[150px] animate-[float_40s_ease-in-out_infinite] pointer-events-none" />
      <div className="hidden lg:block absolute w-[400px] h-[400px] rounded-full bg-white/12 bottom-[500px] left-[800px] animate-[float_18s_ease-in-out_infinite_5s] pointer-events-none" />
      <div className="hidden lg:block absolute w-[300px] h-[300px] rounded-full bg-white/10 top-[60%] left-[15%] animate-[float_20s_ease-in-out_infinite_10s] pointer-events-none" />

      {/* Painel Esquerdo - Visível apenas em desktop */}
      <div className="hidden lg:flex flex-col items-center justify-center p-16 relative z-10">
        <div className="animate-fade-in">
          <h1 className="text-5xl xl:text-6xl font-extrabold text-white mb-4 leading-tight drop-shadow-lg">
            Bem-vindo ao TurboPro!
          </h1>
          <p className="text-lg text-white/90 leading-relaxed font-normal">
            Acesse o sistema utilizando suas credenciais de usuário e senha.
          </p>
        </div>
      </div>

      {/* Painel Direito - Card de Login */}
      <div className="flex flex-col items-center justify-center p-8 relative z-20 bg-white">
        <img
          src="/logo-it-works.png"
          alt="IT Works Logo"
          className="w-[270px] h-auto mb-8  transition-transform duration-300 hover:scale-105 animate-fade-in"
        />

        <div className="bg-white rounded-3xl p-8 sm:p-12 w-full max-w-[460px] shadow-[0_20px_60px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] animate-fade-in">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold bg-linear-to-br from-[#2d3748] to-[#1a202c] bg-clip-text text-transparent mb-2">
              Fazer Login
            </h2>
            <p className="text-sm text-zinc-500">Use suas credenciais para acessar o sistema</p>
          </div>

          <form onSubmit={handleSubmit(handleLogin)} className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <label htmlFor="user" className="text-sm font-semibold text-[#4a5568] mb-1">
                Usuário
              </label>
              <Controller
                name="user"
                control={control}
                render={({ field }) => (
                  <Input
                    id="user"
                    placeholder="Digite seu usuário"
                    autoComplete="username"
                    autoFocus
                    {...field}
                    className="h-12 border-2 border-[#e2e8f0] rounded-xl px-4 text-[15px] transition-all duration-300 bg-[#fafafa] text-[#2d3748] focus:border-[#9ebe3b] focus:bg-white focus:ring-3 focus:ring-[#9ebe3b]/10 placeholder:text-[#a0aec0]"
                  />
                )}
              />
              {errors.user?.message && (
                <div className="text-[#e53e3e] text-xs font-medium mt-1 flex items-center gap-1 animate-fade-in">
                  <span className="flex items-center gap-1">⚠ {errors.user.message}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="pass" className="text-sm font-semibold text-[#4a5568] mb-1">
                Senha
              </label>
              <div className="relative">
                <Controller
                  name="pass"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="pass"
                      placeholder="Digite sua senha"
                      type={passwordVisibility ? 'text' : 'password'}
                      autoComplete="current-password"
                      {...field}
                      className="h-12 border-2 border-[#e2e8f0] rounded-xl px-4 pr-12 text-[15px] transition-all duration-300 bg-[#fafafa] text-[#2d3748] focus:border-[#9ebe3b] focus:bg-white focus:ring-3 focus:ring-[#9ebe3b]/10 placeholder:text-[#a0aec0]"
                    />
                  )}
                />
                <button
                  type="button"
                  onClick={handlePassVisible}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-[#718096] flex items-center justify-center p-2 transition-all duration-200 rounded-lg hover:text-[#9ebe3b] hover:bg-[#9ebe3b]/10 active:scale-95"
                >
                  {passwordVisibility ? (
                    <Eye className="h-5 w-5" />
                  ) : (
                    <EyeOff className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.pass?.message && (
                <div className="text-[#e53e3e] text-xs font-medium mt-1 flex items-center gap-1 animate-fade-in">
                  <span className="flex items-center gap-1">⚠ {errors.pass.message}</span>
                </div>
              )}
              <Link
                to="/esqueci-senha"
                className="self-end text-xs font-medium text-[#7da32e] hover:underline mt-1"
              >
                Esqueci minha senha
              </Link>
            </div>

            {/* Erro de login */}
            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2 animate-fade-in">
                <span>⚠</span>
                <span>{loginError}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-linear-to-br from-[#9ebe3b] to-[#7da32e] text-white font-semibold text-base border-none rounded-xl cursor-pointer transition-all duration-300 shadow-[0_4px_14px_rgba(158,190,59,0.4)] mt-2 hover:shadow-[0_6px_20px_rgba(158,190,59,0.5)] hover:-translate-y-0.5 hover:from-[#a5cc42] hover:to-[#8ab134] active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Entrando...
                </span>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#f0f0f0]">
            <p className="text-xs text-zinc-400 text-center">
              © 2025 IT Works. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
